import axios from "axios";
import { followTicketName } from "../ConfirmTicketing/utils/presaleWindows";

// The series wizard reuses the confirm's drawers. Those speak the server's
// snake_case and reference other rows by id; series rows are camelCase and
// reference each other by name. These adapters translate both ways, keying
// every reference by name so a "ticket type id" in a confirm drawer is a name here.

export const namedTicketTypes = (rows) =>
  rows.map((row) => ({
    id: row.name,
    name: row.name,
    base_name: row.name,
    settlement_amount_two_decimals: row.settlement || row.price || "0",
  }));

// manualTaxIds null means "every tax the venue charges", including ones added later.
export const taxableItemsFrom = (manualTaxIds, manualTaxes) =>
  (manualTaxIds == null ? manualTaxes.map((tax) => tax.id) : manualTaxIds).map((id) => ({ manual_tax_id: id, _destroy: false }));

// Ticking every venue tax goes back to null, as the series' tax checklist always has.
// A venue with no taxes yet has nothing to choose, so the row keeps what it had.
export const manualTaxIdsFrom = (taxableItems, manualTaxes, before = null) => {
  if (manualTaxes.length === 0) return before;
  const ids = (taxableItems || []).filter((item) => !item._destroy).map((item) => item.manual_tax_id);
  const everyTax = manualTaxes.length === ids.length && manualTaxes.every((tax) => ids.includes(tax.id));
  return everyTax ? null : ids;
};

const QUOTE_FIELDS = [
  "final_cost", "custom_charges", "fees", "organizer_service_fee", "round_up_organizer_fee",
  "opendate_service_fee", "processing_fee", "rebate_fee", "taxes",
];

export const quoteFields = (card = {}, cash = {}) =>
  QUOTE_FIELDS.reduce(
    (fields, key) => ({ ...fields, [key]: card[key] || 0, [`cash_${key}`]: cash[key] || 0 }),
    {},
  );

// Prices one row against the series venue (there's no event yet) through the
// confirm's build_ticket endpoint. Resolves to the fee fields the drawers' breakdown reads.
export const fetchFeeQuote = ({ url, csrfToken, venueOwnershipId, kind, form }) => {
  const item = {
    price: Number(form.base_price_two_decimals) || 0,
    free: Boolean(form.free),
    absorb_fees: Boolean(form.absorb_fees),
    round_up: Boolean(form.round_up),
    has_fixed_fees: Boolean(form.has_fixed_fees),
    fixed_fee_amount: form.fixed_fee_amount_two_decimals || null,
    taxable_items_attributes: (form.taxable_items || [])
      .filter((taxableItem) => !taxableItem._destroy)
      .map((taxableItem) => ({ manual_tax_id: taxableItem.manual_tax_id })),
  };
  const body = kind === "add_on" ? { add_on: item } : { ticket_type: { ticket_types: [item] } };
  return axios
    .post(url, { ...body, venue_ownership_id: venueOwnershipId }, { headers: { "X-CSRF-TOKEN": csrfToken } })
    .then(({ data }) => {
      const first = (list) => (Array.isArray(list) ? list[0] : list);
      return quoteFields(first(data.card), first(data.cash));
    });
};

const priceOptionToForm = (option, index) => ({
  ...option,
  key: option.key || option.id || `series-price-option-${index}`,
  newRecord: true,
  base_price_two_decimals: option.price ?? "",
  maximum_tickets_per_order: option.maximumPerOrder ?? "",
});

const priceOptionFromForm = ({ newRecord: _newRecord, base_price_two_decimals: price, maximum_tickets_per_order: maximumPerOrder, ...option }) => ({
  ...option,
  price: String(price ?? "").split(",").join(""),
  maximumPerOrder: maximumPerOrder ?? "",
});

export const ticketToForm = (row, { manualTaxes = [], allowFixedFees = false, defaultCheckInPeriod = "all_time", editing = false }) => ({
  id: editing ? row.name : undefined,
  base_name: row.name,
  free: row.pricing === "free",
  base_price_two_decimals: row.pricing === "free" ? "0.00" : row.price,
  settlement_amount_two_decimals: row.settlement === "" || row.settlement == null ? row.price : row.settlement,
  available: row.inventory,
  minimum_tickets_per_order: row.minimumPerOrder,
  maximum_tickets_per_order: row.maximumPerOrder,
  original_visibility: row.visibility,
  sales_channel: row.salesChannel,
  round_up: Boolean(row.roundUp),
  absorb_fees: Boolean(row.absorbFees),
  allow_fixed_fees: allowFixedFees,
  has_fixed_fees: Boolean(row.hasFixedFees),
  fixed_fee_amount_two_decimals: row.fixedFeeAmount,
  taxable_items: taxableItemsFrom(row.manualTaxIds, manualTaxes),
  check_in_limit: row.checkInLimit,
  check_in_period: row.checkInPeriod || defaultCheckInPeriod,
  hide_qr_code: Boolean(row.hideQrCode),
  accessible_seating: row.accessibleSeating || "",
  description: row.description,
  has_bundled_ticket_type: Boolean(row.bundledTicketTypeName),
  bundled_ticket_type_id: row.bundledTicketTypeName || "",
  should_be_default: Boolean(row.shouldBeDefault),
  opendate_distribution: Boolean(row.opendateDistribution),
  manifested: row.manifested !== false,
  dynamic_pricing_enabled: Boolean(row.dynamicPricingEnabled),
  dynamic_pricing_min: row.dynamicPricingMin,
  dynamic_pricing_max: row.dynamicPricingMax,
  adult_renames_section: false,
  price_options: (row.priceOptions || []).map(priceOptionToForm),
});

export const formToTicket = (form, row, { manualTaxes = [], defaultCheckInPeriod = "all_time" }) => {
  const name = form.base_name || "";
  const price = form.free ? "" : form.base_price_two_decimals || "";
  const settlement = form.settlement_amount_two_decimals || "";
  const renamed = name !== row.name;
  return {
    ...row,
    name,
    pricing: form.free ? "free" : "paid",
    price,
    settlement: form.free || settlement === price ? "" : settlement,
    inventory: form.available ?? "",
    minimumPerOrder: form.minimum_tickets_per_order ?? "",
    maximumPerOrder: form.maximum_tickets_per_order ?? "",
    visibility: form.original_visibility || row.visibility,
    salesChannel: form.sales_channel || row.salesChannel,
    roundUp: Boolean(form.round_up),
    absorbFees: Boolean(form.absorb_fees),
    hasFixedFees: Boolean(form.has_fixed_fees),
    fixedFeeAmount: form.fixed_fee_amount_two_decimals || "",
    manualTaxIds: manualTaxIdsFrom(form.taxable_items, manualTaxes, row.manualTaxIds),
    checkInLimit: form.check_in_limit ?? "",
    checkInPeriod: !row.checkInPeriod && form.check_in_period === defaultCheckInPeriod ? "" : form.check_in_period,
    hideQrCode: Boolean(form.hide_qr_code),
    accessibleSeating: form.accessible_seating || "",
    description: form.description || "",
    bundledTicketTypeName: form.has_bundled_ticket_type ? form.bundled_ticket_type_id || "" : "",
    shouldBeDefault: Boolean(form.should_be_default),
    opendateDistribution: Boolean(form.opendate_distribution),
    manifested: Boolean(form.manifested),
    dynamicPricingEnabled: Boolean(form.dynamic_pricing_enabled),
    dynamicPricingMin: form.dynamic_pricing_min || "",
    dynamicPricingMax: form.dynamic_pricing_max || "",
    priceOptions: form.price_options ? form.price_options.map(priceOptionFromForm) : row.priceOptions,
    presaleWindows: renamed
      ? (row.presaleWindows || []).map((window) => ({ ...window, name: followTicketName(window.name, row.name.trim(), name.trim()) }))
      : row.presaleWindows,
  };
};

export const addOnToForm = (row, { manualTaxes = [], defaultCheckInPeriod = "all_time", editing = false, imagePreviewUrl = null }) => ({
  id: editing ? row.id : undefined,
  base_name: row.name,
  base_price_two_decimals: row.price,
  available: row.inventory,
  seating_chart_category_key: row.sectionKey || null,
  round_up: Boolean(row.roundUp),
  absorb_fees: Boolean(row.absorbFees),
  taxable_items: taxableItemsFrom(row.manualTaxIds, manualTaxes),
  dynamic_pricing_enabled: Boolean(row.dynamicPricingEnabled),
  dynamic_pricing_min: row.dynamicPricingMin,
  dynamic_pricing_max: row.dynamicPricingMax,
  require_ticket_type: Boolean(row.requireTicketType),
  ticket_type_ids: row.requiredTicketTypeNames || [],
  limit_quantity_to_admission_count: row.limitQuantityToAdmissionCount !== false,
  disable_require_ticket_type_with_direct_link: Boolean(row.disableRequireTicketTypeWithDirectLink),
  minimum_per_order: row.minimumPerOrder,
  maximum_per_order: row.maximumPerOrder,
  transferable: row.transferable ?? null,
  accessible_seating: row.accessibleSeating || "",
  start_show_activity_id: row.startActivity || null,
  end_show_activity_id: row.endActivity || null,
  price_points: (row.pricePoints || []).map((point) => ({ id: point.id, name: point.name, price_two_decimals: point.price })),
  description: row.description,
  confirmation_details: row.confirmationDetails,
  image_url: row.imageUrl,
  image_preview_url: imagePreviewUrl,
  image_file_info: row.imageFileName ? { name: row.imageFileName, size: "" } : null,
  check_in_limit: row.checkInLimit,
  check_in_period: row.checkInPeriod || defaultCheckInPeriod,
  hide_qr_code: Boolean(row.hideQrCode),
  sales_channel: row.salesChannel || "Everywhere",
});

// Price changes, the image and the windows are edited through the series'
// own slots, so they stay as the row has them.
export const formToAddOn = (form, row, { manualTaxes = [], defaultCheckInPeriod = "all_time" }) => ({
  ...row,
  name: form.base_name || "",
  price: form.base_price_two_decimals ?? "",
  inventory: form.available ?? "",
  roundUp: Boolean(form.round_up),
  absorbFees: Boolean(form.absorb_fees),
  manualTaxIds: manualTaxIdsFrom(form.taxable_items, manualTaxes, row.manualTaxIds),
  dynamicPricingEnabled: Boolean(form.dynamic_pricing_enabled),
  dynamicPricingMin: form.dynamic_pricing_min || "",
  dynamicPricingMax: form.dynamic_pricing_max || "",
  requireTicketType: Boolean(form.require_ticket_type),
  requiredTicketTypeNames: form.require_ticket_type ? (form.ticket_type_ids || []).map(String) : [],
  limitQuantityToAdmissionCount: form.limit_quantity_to_admission_count !== false,
  disableRequireTicketTypeWithDirectLink: Boolean(form.disable_require_ticket_type_with_direct_link),
  minimumPerOrder: form.minimum_per_order ?? "",
  maximumPerOrder: form.maximum_per_order ?? "",
  transferable: form.transferable ?? null,
  accessibleSeating: form.accessible_seating || "",
  description: form.description || "",
  confirmationDetails: form.confirmation_details || "",
  checkInLimit: form.check_in_limit ?? "",
  checkInPeriod: !row.checkInPeriod && form.check_in_period === defaultCheckInPeriod ? "" : form.check_in_period,
  hideQrCode: Boolean(form.hide_qr_code),
  salesChannel: form.sales_channel || "Everywhere",
});

// A series code holds one discount and its type; the confirm form holds an
// amount or a percentage. Windows stay on the form under the series' own keys,
// for the series' window slot to edit.
export const promoToForm = (row, { editing = false } = {}) => ({
  id: editing ? row.id : undefined,
  name: row.name,
  applies_to_ticket_types: Boolean(row.appliesToTicketTypes),
  limit_to_one_ticket_per_order: Boolean(row.limitToOneTicketPerOrder),
  discounted_tickets_per_order: row.discountedTicketsPerOrder ?? null,
  reveal_hidden_tickets: Boolean(row.revealHiddenTickets),
  reveal_all_hidden_tickets: Boolean(row.revealAllHiddenTickets),
  ticket_type_ids: row.revealedTicketTypeNames || [],
  event_hold_ids: row.revealedHoldNames || [],
  discounts_only_selected_ticket_types: Boolean(row.discountsOnlySelectedTicketTypes),
  discounted_ticket_type_ids: row.discountedTicketTypeNames || [],
  applies_to_add_ons: Boolean(row.appliesToAddOns),
  limit_to_one_add_on_per_order: Boolean(row.limitToOneAddOnPerOrder),
  unlimited: Boolean(row.unlimited),
  limit: row.limit,
  amount_two_decimals: row.discountType === "amount" ? row.discount : null,
  percentage: row.discountType === "amount" ? null : row.discount,
  applies_to_entire_order: Boolean(row.appliesToEntireOrder),
  should_be_default: Boolean(row.shouldBeDefault),
  availability: "Based on show details",
  startActivity: row.startActivity,
  endActivity: row.endActivity,
  startOffsetMinutes: row.startOffsetMinutes,
  endOffsetMinutes: row.endOffsetMinutes,
});

export const formToPromo = (form, row) => {
  const amount = form.amount_two_decimals != null && form.amount_two_decimals !== "";
  return {
    ...row,
    name: (form.name || "").trim().toUpperCase(),
    appliesToTicketTypes: Boolean(form.applies_to_ticket_types),
    limitToOneTicketPerOrder: Boolean(form.limit_to_one_ticket_per_order),
    discountedTicketsPerOrder: form.limit_to_one_ticket_per_order ? (form.discounted_tickets_per_order ?? null) : null,
    revealHiddenTickets: Boolean(form.reveal_hidden_tickets),
    revealAllHiddenTickets: Boolean(form.reveal_all_hidden_tickets),
    revealedTicketTypeNames: (form.ticket_type_ids || []).map(String),
    revealedHoldNames: (form.event_hold_ids || []).map(String),
    discountsOnlySelectedTicketTypes: Boolean(form.discounts_only_selected_ticket_types),
    discountedTicketTypeNames: (form.discounted_ticket_type_ids || []).map(String),
    appliesToAddOns: Boolean(form.applies_to_add_ons),
    limitToOneAddOnPerOrder: Boolean(form.limit_to_one_add_on_per_order),
    unlimited: Boolean(form.unlimited),
    limit: form.unlimited ? "" : String(form.limit ?? ""),
    discountType: amount ? "amount" : "percentage",
    discount: String((amount ? form.amount_two_decimals : form.percentage) ?? ""),
    appliesToEntireOrder: amount && Boolean(form.applies_to_entire_order),
    shouldBeDefault: Boolean(form.should_be_default),
    startActivity: form.startActivity,
    endActivity: form.endActivity,
    startOffsetMinutes: form.startOffsetMinutes,
    endOffsetMinutes: form.endOffsetMinutes,
  };
};
